import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { CircleCheck, CircleX, FileText, FolderOpen, LucideAngularModule, Pencil, Plus, Trash2 } from 'lucide-angular';
import { DocumentConfig } from '../../../core/models/domain.models';
import { ToastService } from '../../../shared/ui/toast.service';
import { VendorDocumentsService } from '../services/vendor-documents.service';

@Component({
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  styleUrl: './vendor-document-config.page.scss',
  template: `
    <div class="vendor-page">
      <div class="vendor-shell p-3 p-lg-4">
        <div class="d-flex flex-column flex-xl-row justify-content-between gap-3 align-items-xl-center">
          <div class="d-flex align-items-center gap-3">
            <span class="title-badge">
              <lucide-icon [img]="FileTextIcon" [size]="20"></lucide-icon>
            </span>
            <div>
              <div class="vendor-page-header__eyebrow">Master Setting</div>
              <h1 class="page-title-lite m-0">Document Configuration</h1>
              <p class="vendor-page-subtitle mb-0">Configure required documents by module, client type, and upload rules.</p>
            </div>
          </div>

          <button class="btn btn-sm create-btn primary" (click)="router.navigateByUrl('/document-configurations/new')">
            <lucide-icon [img]="PlusIcon" [size]="14"></lucide-icon><span class="ms-2">ADD CONFIGURATION</span>
          </button>
        </div>
      </div>

      <div class="panel-card p-3 p-lg-4">
        <div class="table-wrap">
          @if (configs().length === 0) {
            <div class="empty-state text-center py-5">
              <lucide-icon [img]="FolderOpenIcon" [size]="36" class="empty-state__icon"></lucide-icon>
              <div class="mt-2">No document configurations found.</div>
            </div>
          } @else {
            <div class="table-responsive">
              <table class="table align-middle mb-0">
                <thead class="table-head">
                  <tr>
                    <th>S.No.</th>
                    <th>Module Name</th>
                    <th>Client Type</th>
                    <th>Document Name</th>
                    <th>Allowed File Types</th>
                    <th>Mandatory</th>
                    <th>Max File Size</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  @for (config of configs(); track config.id; let i = $index) {
                    <tr>
                      <td>{{ i + 1 }}</td>
                      <td><span class="metric-pill module-pill">{{ config.module_name || '-' }}</span></td>
                      <td>
                        <div class="d-flex flex-wrap gap-1">
                          @for (type of clientTypeTags(config); track type) {
                            <span class="metric-pill client-pill">{{ type }}</span>
                          }
                        </div>
                      </td>
                      <td class="fw-semibold">{{ config.doc_title }}</td>
                      <td><span class="metric-pill type-pill">{{ config.allowed_file_types || config.file_type || '-' }}</span></td>
                      <td>
                        <span class="metric-pill flag" [class.success]="config.mandatory" [class.muted]="!config.mandatory">
                          {{ config.mandatory ? 'Yes' : 'No' }}
                        </span>
                      </td>
                      <td>{{ config.max_file_size_mb || 10 }} MB</td>
                      <td>
                        <span class="status-pill text-capitalize" [class.approved]="(config.status || 'Active') === 'Active'" [class.rejected]="(config.status || 'Active') !== 'Active'">
                          <lucide-icon [img]="(config.status || 'Active') === 'Active' ? CircleCheckIcon : CircleXIcon" [size]="13"></lucide-icon>
                          {{ config.status || 'Active' }}
                        </span>
                      </td>
                      <td>
                        <div class="d-flex gap-2">
                          <button class="action-btn edit" title="Edit" (click)="router.navigateByUrl('/document-configurations/' + config.id + '/edit')">
                            <lucide-icon [img]="PencilIcon" [size]="14"></lucide-icon>
                          </button>
                          <button class="action-btn delete" title="Delete" (click)="remove(config)">
                            <lucide-icon [img]="Trash2Icon" [size]="14"></lucide-icon>
                          </button>
                        </div>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </div>
      </div>
    </div>
  `,
})
export class VendorDocumentConfigPage {
  protected readonly router = inject(Router);
  private readonly documentsService = inject(VendorDocumentsService);
  private readonly toast = inject(ToastService);

  protected readonly FileTextIcon = FileText;
  protected readonly PlusIcon = Plus;
  protected readonly PencilIcon = Pencil;
  protected readonly Trash2Icon = Trash2;
  protected readonly FolderOpenIcon = FolderOpen;
  protected readonly CircleCheckIcon = CircleCheck;
  protected readonly CircleXIcon = CircleX;

  protected readonly configs = signal<DocumentConfig[]>([]);

  constructor() {
    this.load();
  }

  private load(): void {
    this.documentsService.listConfigs().subscribe((configs) => this.configs.set(configs));
  }

  protected clientTypeTags(config: DocumentConfig): string[] {
    return (config.client_types || config.client_type || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  protected remove(config: DocumentConfig): void {
    if (!confirm('Delete this document config?')) return;
    this.documentsService.deleteConfig(config.id).subscribe({
      next: () => {
        this.toast.show('Document config deleted', '', 'success');
        this.load();
      },
      error: () => this.toast.show('Failed to delete config', '', 'danger'),
    });
  }
}
